/**
 * Validate a required field value. Optionally checks max length.
 * @param {string|Array} val - The value to validate
 * @param {function} errorHandler - setState function for error boolean (optional)
 * @param {function} helperHandler - setState function for helper text (optional)
 * @param {number|null} maxLength - Optional maximum length
 * @param {string} fieldName - Field name for error messages
 * @returns {boolean} true if valid
 */
const noop = () => {}
export const validateField = (val, errorHandler = noop, helperHandler = noop, maxLength = null, fieldName = 'Field') => {
    if (val == null || val.length === 0) {
        errorHandler(true)
        helperHandler('Required Field!')
        return false
    } else if (maxLength && val.length > maxLength) {
        errorHandler(true)
        helperHandler(`${fieldName} must be ${maxLength} characters or less (currently ${val.length})`)
        return false
    } else {
        errorHandler(false)
        helperHandler('')
        return true
    }
}

/**
 * True when `val` parses as an absolute URL. Module-private: the only intended
 * caller is validateUrlField below.
 */
const isAbsoluteUrl = (val) => {
    try {
        return Boolean(new URL(val))
    } catch (error) {
        return false
    }
}

/**
 * Validate a required URL field.
 *
 * Exists because the Course/Video/Podcast admin modals' Approve/Disapprove
 * button is no longer a `type='submit'` (issue #390): the browser therefore
 * never runs the parent Link input's `type='url'` constraint check on the
 * approve path, so that format guard has to happen in JS instead. Without it an
 * admin could approve a scheme-less value like `example.com/course` and the
 * backend URLField would 400.
 *
 * Empty fails with the SAME message validateField produces, so the empty case
 * is unchanged. `new URL(value)` is the format test because it mirrors the
 * browser's `type='url'` semantics closely -- it accepts `https://foo` and
 * rejects `example.com/x` -- and exists in both jsdom and every supported
 * browser.
 *
 * @param {string} val - The value to validate
 * @param {function} errorHandler - setState function for error boolean (optional)
 * @param {function} helperHandler - setState function for helper text (optional)
 * @param {number|null} maxLength - Optional maximum length
 * @param {string} fieldName - Field name for error messages
 * @returns {boolean} true if valid
 */
export const validateUrlField = (val, errorHandler = noop, helperHandler = noop, maxLength = null, fieldName = 'Field') => {
    if (!validateField(val, errorHandler, helperHandler, maxLength, fieldName)) {
        return false
    } else if (!isAbsoluteUrl(val)) {
        errorHandler(true)
        helperHandler('Enter a valid URL, including https://')
        return false
    } else {
        errorHandler(false)
        helperHandler('')
        return true
    }
}

/**
 * The single frontend character cap for resource descriptions. Frontend-only by
 * design: `Resource.description` is an unbounded TextField (backend/resources/
 * models.py:38) and adding a DB max_length would reject existing production rows
 * on their next save (issue #472, out of scope).
 */
export const DESCRIPTION_MAX_LENGTH = 500

/**
 * Effective cap for a description field, grandfathering whatever is already
 * stored. Rows longer than the cap exist today (the Django admin applies none),
 * and they must stay editable and saveable. Raises the ceiling to the stored
 * value's length when that exceeds DESCRIPTION_MAX_LENGTH, so an over-cap row
 * stays editable and saveable without being allowed to grow past its existing
 * length -- it may be replaced by any other value up to that same length, not
 * only a prefix of the original.
 * @param {string} storedValue - the description as loaded from the API
 * @returns {number}
 */
export const descriptionLimit = (storedValue) =>
    Math.max(DESCRIPTION_MAX_LENGTH, (storedValue || '').length)

/**
 * Validate an OPTIONAL field: empty always passes, over-length always fails.
 * Deliberately separate from validateField, which treats length 0 as an error
 * and is relied on for that by every required field in the app.
 * @param {string} val
 * @param {function} errorHandler - setState function for error boolean (optional)
 * @param {function} helperHandler - setState function for helper text (optional)
 * @param {number|null} maxLength - Optional maximum length
 * @param {string} fieldName - Field name for error messages
 * @returns {boolean} true if valid
 */
export const validateOptionalField = (val, errorHandler = noop, helperHandler = noop, maxLength = null, fieldName = 'Field') => {
    const text = val || ''
    if (maxLength && text.length > maxLength) {
        errorHandler(true)
        helperHandler(`${fieldName} must be ${maxLength} characters or less (currently ${text.length})`)
        return false
    }
    errorHandler(false)
    helperHandler('')
    return true
}

/**
 * Validates required resource tag groups used across resource request/edit forms.
 * Required groups: content, admin category, experience, demographic. T-CAIREM
 * resource type and persona tags are additionally required when `isTcairem` is true.
 * @param {Object} tags
 * @param {Array} tags.contentTags
 * @param {Array} tags.adminCategoryTags
 * @param {Array} tags.experienceTags
 * @param {Array} tags.demographicTags
 * @param {Array} tags.tcairemTags - T-CAIREM resource type tags, required only when isTcairem
 * @param {Array} tags.personaTags - T-CAIREM persona tags, required only when isTcairem
 * @param {boolean} tags.isTcairem - Whether this resource is flagged as a T-CAIREM resource
 * @param {number} maxLength - Optional max tags per group
 * @returns {boolean} true when all required tag groups are valid
 */
export const validateRequiredResourceTags = ({
    contentTags = [],
    adminCategoryTags = [],
    experienceTags = [],
    demographicTags = [],
    tcairemTags = [],
    personaTags = [],
    isTcairem = false,
}, maxLength = 5) => (
    validateField(contentTags, undefined, undefined, maxLength, 'Content Tags') &&
    validateField(adminCategoryTags, undefined, undefined, maxLength, 'Admin Category Tags') &&
    validateField(experienceTags, undefined, undefined, maxLength, 'Experience Tags') &&
    validateField(demographicTags, undefined, undefined, maxLength, 'Demographic Tags') &&
    (!isTcairem || validateField(tcairemTags, undefined, undefined, maxLength, 'T-CAIREM Resource Type Tags')) &&
    (!isTcairem || validateField(personaTags, undefined, undefined, maxLength, 'T-CAIREM Persona Tags'))
)

/**
 * Maps tag arrays to their name strings for API submission.
 * @param {Object} tagState - Object with tag state arrays
 * @param {Array} tagState.tcairemTags - T-CAIREM resource type tags
 * @param {Array} tagState.personaTags - T-CAIREM persona tags
 * @param {boolean} tagState.isTcairem - Whether this resource is flagged as a T-CAIREM resource;
 *   when false, tcairem_type_tags/tcairem_persona_tags are emitted as empty arrays to clear
 *   any existing tags
 * @returns {Object} Object with tag arrays mapped to name strings
 */
export const collectTagData = ({
    contentTags = [],
    medicalTags = [],
    adminCategoryTags = [],
    experienceTags = [],
    demographicTags = [],
    tcairemTags = [],
    personaTags = [],
    isTcairem = false,
}) => ({
    content_tags: contentTags.map(tag => tag.name ?? tag),
    medical_specialty_tags: medicalTags.map(tag => tag.name ?? tag),
    admin_category_tags: adminCategoryTags.map(tag => tag.name ?? tag),
    experience_tags: experienceTags.map(tag => tag.name ?? tag),
    demographic_tags: demographicTags.map(tag => tag.name ?? tag),
    // Always emit the key: admin modals spread `...props` into payloads, so an
    // unconditional [] here is what actually clears tags when the flag is off.
    tcairem_type_tags: isTcairem ? tcairemTags.map(tag => tag.name ?? tag) : [],
    tcairem_persona_tags: isTcairem ? personaTags.map(tag => tag.name ?? tag) : [],
})

/**
 * The fields a nested sub-resource row (course lesson, video part, podcast
 * episode) marks `required` in markup -- the four `required` inputs in the
 * sub-resource row block of AdminCourseModal.js, AdminVideoModal.js and
 * AdminPodcastModal.js.
 */
export const SUB_RESOURCE_REQUIRED_FIELDS = ['title', 'authors', 'link', 'pub_date']

/**
 * Validate a resource's nested sub-resource rows.
 *
 * Before issue #390 these rows were guarded ONLY by native browser form
 * validation, which fired because both admin-modal action buttons were
 * `type='submit'`. Approve/Disapprove is now `type='button'`, so it no longer
 * triggers that native pass; this helper restores the guard in JS and is called
 * from BOTH the save and the approve path in the Course, Video and Podcast
 * admin modals.
 *
 * Deliberate limits:
 *  - Emptiness only, and NO trim(). That mirrors exactly what the native
 *    `required` attribute checks, and matches validateField's own
 *    `val.length === 0` test. Trimming would be stricter than the guard it
 *    replaces.
 *  - No URL-format check, despite the sub-row Link input being `type='url'`.
 *    Stored legacy rows may hold values that do not parse as URLs; blocking
 *    approval on them would be a new failure mode beyond #390's scope.
 *
 * A nullish `rows` (null or undefined) is treated as "no rows" and passes: the
 * modals initialise these lists from optional props, so an absent list is a live
 * shape here and must not throw inside the save/approve path.
 *
 * @param {Array<Object>|null|undefined} rows - sub-resource rows, each {title, authors, link, pub_date}
 * @returns {boolean} true when every row has all four required fields non-empty
 */
export const validateSubResourceRows = (rows) =>
    (rows ?? []).every((row) => SUB_RESOURCE_REQUIRED_FIELDS.every(
        (field) => String(row?.[field] ?? '').length > 0
    ))
